/**
 * Кэш уроков, подгруженных заранее, — только в памяти.
 *
 * Урок, который вот-вот понадобится, кладётся сюда фоновой подгрузкой, а
 * экран урока забирает его без запроса и лоадера. Хранится недолго
 * (`LESSON_PREFETCH_TTL_MS`): за это время повторы FSRS устареть не успевают,
 * а после — урок честно спросим заново.
 *
 * Взятый урок живёт ещё пару секунд (`TAKEN_GRACE_MS`): экран урока может
 * смонтироваться дважды подряд (StrictMode, быстрый повторный рендер) —
 * оба раза без запроса.
 */

import { create } from 'zustand';

import type { CurriculumLessonView } from '@aptile/shared';

/** Дольше урок не держим: его повторы могли устареть. */
export const LESSON_PREFETCH_TTL_MS = 10 * 60 * 1000;
/** Сколько взятый урок ещё доступен — на двойной монтаж StrictMode. */
const TAKEN_GRACE_MS = 2_000;

export interface PrefetchedLesson {
  readonly view: CurriculumLessonView;
  readonly fetchedAt: number;
}

/** Ключ кэша: один урок у разных учеников — разные записи. */
export function lessonPrefetchKey(userId: string, skillId: string): string {
  return `${userId}|${skillId}`;
}

/** Свежая ли запись. `now` — для тестов и для чтения в момент рендера. */
export function isPrefetchedLessonFresh(entry: PrefetchedLesson | undefined, now = Date.now()): boolean {
  return entry !== undefined && now - entry.fetchedAt <= LESSON_PREFETCH_TTL_MS;
}

interface LessonPrefetchState {
  readonly lessons: Readonly<Record<string, PrefetchedLesson>>;
  put: (key: string, view: CurriculumLessonView, now?: number) => void;
  /** Забрать свежий урок; устаревший выбрасывается. `null` — нет или протух. */
  take: (key: string, now?: number) => CurriculumLessonView | null;
  has: (key: string, now?: number) => boolean;
  drop: (key: string) => void;
}

/** Взятый урок удаляется через `TAKEN_GRACE_MS`, несвежий — по TTL. */
const evictionTimers = new Map<string, ReturnType<typeof setTimeout>>();
const ttlTimers = new Map<string, ReturnType<typeof setTimeout>>();

const clearTimers = (key: string): void => {
  const eviction = evictionTimers.get(key);
  if (eviction !== undefined) clearTimeout(eviction);
  evictionTimers.delete(key);
  const ttl = ttlTimers.get(key);
  if (ttl !== undefined) clearTimeout(ttl);
  ttlTimers.delete(key);
};

export const useLessonPrefetchStore = create<LessonPrefetchState>()((set, get) => ({
  lessons: {},

  put: (key, view, now = Date.now()) => {
    clearTimers(key);
    set((state) => ({ lessons: { ...state.lessons, [key]: { view, fetchedAt: now } } }));
    // Точка «урок готов» гаснет сама, даже если урок так и не открыли.
    ttlTimers.set(
      key,
      setTimeout(() => {
        ttlTimers.delete(key);
        get().drop(key);
      }, LESSON_PREFETCH_TTL_MS),
    );
  },

  take: (key, now = Date.now()) => {
    const entry = get().lessons[key];
    if (entry === undefined || !isPrefetchedLessonFresh(entry, now)) {
      if (entry !== undefined) get().drop(key);
      return null;
    }
    // Не удаляем сразу: экран урока может смонтироваться повторно.
    const previous = evictionTimers.get(key);
    if (previous !== undefined) clearTimeout(previous);
    evictionTimers.set(
      key,
      setTimeout(() => {
        evictionTimers.delete(key);
        get().drop(key);
      }, TAKEN_GRACE_MS),
    );
    return entry.view;
  },

  has: (key, now = Date.now()) => isPrefetchedLessonFresh(get().lessons[key], now),

  drop: (key) => {
    clearTimers(key);
    set((state) => {
      if (state.lessons[key] === undefined) return state;
      const { [key]: _removed, ...rest } = state.lessons;
      return { lessons: rest };
    });
  },
}));
